import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { ExperienceBadge } from '@/components/experience-badge';
import {
  AuthContainer,
  type DateItem,
  DatesStep,
  type WishlistItem,
  WishlistStep,
} from '@/components/shared';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePreRegistration } from '@/hooks/mutations/use-pre-registration';
import { useInviteStore } from '@/stores';

import { EmailStep } from './-components/email-step';
import { MagicLinkSent } from './-components/magic-link-sent';
import { StepIndicator } from './-components/step-indicator';

export const Route = createFileRoute('/invite/onboarding/')({
  component: InviteOnboardingPage,
});

const nameSchema = z.object({
  name: z.string().min(2, 'O nome deve ter pelo menos 2 caracteres'),
});

type NameFormValues = z.infer<typeof nameSchema>;

type Step = 'email' | 'magic-link-sent' | 'name' | 'dates' | 'wishlist';

function InviteOnboardingPage() {
  const navigate = useNavigate();
  const {
    inviteToken,
    creatorName,
    guestData,
    setGuestEmail,
    setGuestName,
    setGuestDates,
    setGuestWishlistItems,
  } = useInviteStore();

  const hasHydrated = useSyncExternalStore(
    useInviteStore.persist.onFinishHydration,
    () => useInviteStore.persist.hasHydrated(),
  );

  const preRegistrationMutation = usePreRegistration();

  const [step, setStep] = useState<Step>('email');
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<NameFormValues>({
    resolver: zodResolver(nameSchema),
    defaultValues: { name: guestData.name },
  });

  // Redirect if no invite token (only after hydration)
  useEffect(() => {
    if (hasHydrated && (!inviteToken || !creatorName)) {
      navigate({ to: '/' });
    }
  }, [hasHydrated, inviteToken, creatorName, navigate]);

  // Restore step from store
  useEffect(() => {
    if (guestData.email && step === 'email') {
      if (guestData.name) {
        if (guestData.dates.length > 0) {
          setStep('wishlist');
        } else {
          setStep('dates');
        }
      } else {
        setStep('name');
      }
    }
  }, [guestData, step]);

  const handleExistingUser = (email: string) => {
    setGuestEmail(email);
    setSentToEmail(email);
    setStep('magic-link-sent');
  };

  const handleNewUser = (email: string) => {
    setGuestEmail(email);
    setStep('name');
  };

  const handleTryAgain = () => {
    setSentToEmail(null);
    setStep('email');
  };

  const handleNameSubmit = (data: NameFormValues) => {
    setGuestName(data.name);
    setStep('dates');
  };

  const handleDatesNext = (dates: DateItem[]) => {
    setGuestDates(dates);
    setStep('wishlist');
  };

  const handleWishlistNext = async (wishlistItems: WishlistItem[]) => {
    setGuestWishlistItems(wishlistItems);

    const { guestData: currentData, inviteToken: currentToken } =
      useInviteStore.getState();

    try {
      await preRegistrationMutation.mutateAsync({
        email: currentData.email,
        name: currentData.name,
        dates: currentData.dates,
        wishlist_items: wishlistItems.length > 0 ? wishlistItems : undefined,
        invite_token: currentToken!,
      });
      navigate({ to: '/registration/success' });
    } catch (error) {
      if (error instanceof HTTPError && error.response.status === 409) {
        toast.error('Este e-mail já está cadastrado.');
      } else {
        toast.error('Erro ao criar conta. Tente novamente.');
      }
    }
  };

  if (!hasHydrated || !inviteToken || !creatorName) {
    return null;
  }

  if (step === 'email') {
    return (
      <AuthContainer>
        <EmailStep
          creatorName={creatorName}
          onExistingUser={handleExistingUser}
          onNewUser={handleNewUser}
        />

        <p className="text-muted-foreground text-center text-sm">
          Já tem uma conta?{' '}
          <Link to="/login" className="text-primary hover:underline">
            Entrar
          </Link>
        </p>

        <div className="flex flex-col space-y-2 text-center">
          <Link
            to="/"
            className="text-muted-foreground hover:text-primary text-sm"
          >
            Voltar para a página inicial
          </Link>
          <ExperienceBadge />
        </div>
      </AuthContainer>
    );
  }

  if (step === 'magic-link-sent' && sentToEmail) {
    return (
      <AuthContainer>
        <MagicLinkSent email={sentToEmail} onTryAgain={handleTryAgain} />
        <ExperienceBadge />
      </AuthContainer>
    );
  }

  // Steps 2-4 for new users
  const currentStepNumber = step === 'name' ? 1 : step === 'dates' ? 2 : 3;

  return (
    <AuthContainer>
      <StepIndicator currentStep={currentStepNumber} totalSteps={3} />

      {step === 'name' && (
        <form onSubmit={handleSubmit(handleNameSubmit)} className="space-y-6">
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Qual é o seu nome?
            </h1>
            <p className="text-muted-foreground text-sm">
              Esse nome aparecerá para {creatorName}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              type="text"
              placeholder="Seu nome"
              autoComplete="name"
              autoFocus
              {...register('name')}
            />
            <FormError message={errors.name?.message} />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => {
                setValue('name', '');
                setStep('email');
              }}
            >
              Voltar
            </Button>
            <Button type="submit" className="flex-1">
              Continuar
            </Button>
          </div>
        </form>
      )}

      {step === 'dates' && (
        <DatesStep
          defaultValue={guestData.dates}
          onNext={handleDatesNext}
          onBack={() => setStep('name')}
        />
      )}

      {step === 'wishlist' && (
        <WishlistStep
          defaultValue={guestData.wishlistItems}
          continueLabel="Finalizar"
          skipLabel="Finalizar (pular)"
          isLoading={preRegistrationMutation.isPending}
          loadingLabel="Criando conta..."
          onNext={handleWishlistNext}
          onBack={() => setStep('dates')}
        />
      )}

      <p className="text-muted-foreground text-center text-sm">
        Já tem uma conta?{' '}
        <Link to="/login" className="text-primary hover:underline">
          Entrar
        </Link>
      </p>

      <div className="flex flex-col space-y-2 text-center">
        <Link
          to="/"
          className="text-muted-foreground hover:text-primary text-sm"
        >
          Voltar para a página inicial
        </Link>
        <ExperienceBadge />
      </div>
    </AuthContainer>
  );
}
